/**
 * RetellPage — «Пересказ близко к тексту» (раздел «Связная речь»).
 *
 * На экране РАССКАЗ целиком (этап чтения), затем пересказ ВСЛУХ — счёт
 * считает сервер по эталону рассказа (cortivox.retell_milestones: вехи,
 * связки «A + B», синонимы, ASR-варианты). Ключи и формулировки вех на
 * клиент НЕ уходят: пациент видит только текст рассказа и наводящие вопросы.
 *
 * Сценарий:
 *   1. ВЫБОР РАССКАЗА (t01..t04 — банк retell_stories; остальные 16 — после
 *      обкатки). ?story=t04 открывает рассказ сразу.
 *   2. ЧТЕНИЕ: врач читает рассказ пациенту вслух (или пациент читает сам).
 *      МИКРОФОН НА ЭТОМ ЭТАПЕ НЕ ВКЛЮЧАЕТСЯ (не пишем чтение в транскрипт!) —
 *      это осознанное отличие от «Беглости»/«Зрительного восприятия», где
 *      запись стартует сразу.
 *   3. ПЕРЕСКАЗ: кнопка «Начать пересказ» запрашивает микрофон и включает
 *      распознавание (ru-RU, continuous; Chrome останавливается на паузах —
 *      перезапускаем в onend). Распознавание под СТОРОЖЕМ: параллельно
 *      меряется громкость микрофона, и если речь в микрофоне есть, а текст
 *      не растёт дольше 8 с — сеанс тихо пересоздаётся (Chrome умеет молча
 *      терять поток распознавания; финальный текст при этом не теряется).
 *      Кнопка «Подсказка» — наводящий вопрос по МЕСТУ ОСТАНОВКИ: клиент раз
 *      в несколько секунд отдаёт промежуточную стенограмму на
 *      POST /api/trainers/retell/preview, сервер возвращает ТОЛЬКО номера
 *      закрытых вех, и подсказка target'ит «ГРАНИЦУ РАССКАЗА» — первую
 *      незакрытую веху ПОСЛЕ последней закрытой, то есть то, что должно
 *      прозвучать СЕЙЧАС. Вехи позади границы пациент уже прошёл: даже если
 *      эталон их не зачёл (синоним мимо, ASR исказил), переспрашивать их
 *      нельзя — пропуски покажет итоговый отчёт. Предпросчёт недоступен —
 *      идём по порядку текста от последней известной границы (пометка
 *      «· по порядку»). Лимит
 *      подсказок — config.hintLimit (эталон: 8–12). Если пациент молчит
 *      дольше config.silenceSec (по умолчанию 30 с) — попап
 *      «Нужна подсказка?» с кнопкой выдачи наводящего вопроса.
 *      Стенограмма во время записи занимает всё свободное окно (не 1/3 экрана)
 *      и прокручена к свежему тексту.
 *   4. Финиш («Готово»; лимита времени НЕТ — решено 03.11.2026) →
 *      transcript + протокол уходят на
 *      POST /api/trainers/retell {stepId: 'r01'..'r04', elapsedMs,
 *      thinkingMs, hintsUsed} — сервер считает вехи/блоки/флаги и пишет
 *      отчёт в ОБЩУЮ таблицу trainer_reports (algorithm_version='retell-web-1').
 *   5. РЕЗУЛЬТАТ: процент ядра, «Названо X из N» по блокам, флаги причинных
 *      цепочек, список «частично». Врач дополнительно видит таблицу вех
 *      (формулировка, статус, чем зачтено) и «Детали для врача» — сверка
 *      зачёта по стенограмме (правило «Обнаружение ≠ оценка» эталона).
 *
 * Маршрут: /retell — пациент сам; /retell?patientId=UUID — запуск врачом из
 * карточки пациента; /retell?story=t04&patientId=… — сразу к рассказу.
 *
 * Правка 04.11.2026 (сборка 04.11-c): ПОДСКАЗКУ ВЫБИРАЕТ СЕРВЕР.
 *   POST /api/trainers/retell/hint {storyCode, transcript, given, hintsUsed}
 *   → {target:{num,question}, mode, frontier, closed, ...}. Клиент больше не
 *   считает «границу рассказа» и не гоняет периодический предпросчёт: логика
 *   в api/app/scoring/retell_hint.py (с тестами). Если сервер недоступен —
 *   НИКАКИХ догадок по порядку/объёму речи: показываем причину («HTTP 404» /
 *   «нет связи») и врач выбирает вопрос вручную из списка. Каждое нажатие
 *   пишется в протокол (hintEvents: веха, время, что было закрыто ДО вопроса).
 *
 * Правки 03.11.2026 (по итогам теста):
 *  1) сеансы распознавания пересоздаются СТРОГО по одному: старый глушится,
 *     пауза ~300 мс — и только потом открывается новый. Раньше два живых
 *     сеанса одновременно приводили то к «not-allowed» (упражнение падало
 *     в ошибку посреди пересказа), то к молчащему сеансу (текст переставал
 *     распознаваться). onend теперь открывает СВЕЖИЙ сеанс, а не
 *     переиспользует старый экземпляр (тот умеет молча умирать);
 *  2) «not-allowed» посреди пересказа больше не убивает упражнение: текст
 *     сохраняется, жёлтая плашка «восстанавливаем…», сторож возвращает
 *     распознавание; жёсткая ошибка — только если микрофон не разрешили
 *     в самом начале;
 *  3) подсказки: сбой предпросчёта больше не стирает последнюю оценку
 *     закрытых вех — вопрос идёт по МЕСТУ ОСТАНОВКИ, пока оценке ≤ 90 с;
 *     если подсказка выдаётся «по порядку» (предпросчёта нет), в карточке
 *     подсказки видна пометка «· по порядку».
 *  4) подсказка target'ит ГРАНИЦУ РАССКАЗА: первая незакрытая веха ПОСЛЕ
 *     последней закрытой («что должно прозвучать сейчас»), а не первая
 *     незакрытая по порядку текста. Раньше веха, которую пациент уже
 *     прошёл, но эталон не зачёл (синоним мимо, ASR исказил, связку
 *     «съело» другое упоминание), уводила вопрос НАЗАД — в уже
 *     рассказанное. Повторное нажатие на том же месте повторяет вопрос
 *     ТОЙ ЖЕ вехи (пациент всё ещё на ней), пометка «· повтор вопроса»,
 *     а не перепрыгивает через место остановки; по протухшей оценке —
 *     пометка «· по последней оценке»; fallback «по порядку» стартует
 *     с последней известной границы, а не с начала рассказа.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  BrainCircuit,
  CheckCircle2,
  CircleDashed,
  Flag,
  Lightbulb,
  Loader2,
  Mic,
  MicOff,
  RotateCcw,
} from 'lucide-react';
// Клиент — общий cortivoxApi.js (тот же request-хелпер с Bearer-токеном).
import {
  getAuthUserId,
  getRetellStories,
  getRetellStory,
  localToday,
  requestRetellHint,
  saveRetellReport,
} from '../lib/cortivoxApi';
import { createRecognition, isRecognitionSupported } from '../services/speechRecognition';

const MAX_WORDS = 800;                       // защита от аномально длинного transcript
const DEFAULT_HINT_LIMIT = 10;               // лимит подсказок 8–12 (эталон)
const DEFAULT_SILENCE_SEC = 30;              // тишина → попап «нужна подсказка?»
const WATCHDOG_TICK_MS = 2000;               // период сторожа распознавания
const WATCHDOG_STALL_MS = 8000;              // речь в микрофоне есть, текста нет столько — перезапуск
const LOUD_RMS = 0.01;                       // порог «в микрофоне речь, а не тихий фон»
const BUILD_ID = '04.11-c';                  // метка сборки: видна на экране выбора и в консоли

const stepIdForStory = (num) => `r${String(Number(num) || 1).padStart(2, '0')}`;

const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const STATUS = {
  closed: { icon: CheckCircle2, text: 'закрыта', cls: 'text-emerald-600' },
  partial: { icon: CircleDashed, text: 'частично', cls: 'text-amber-600' },
  missed: { icon: CircleDashed, text: 'не воспроизведена', cls: 'text-cvx-muted-foreground' },
};

const hintFromError = (err) => {
  const status = err?.status ?? err?.response?.status;
  if (status === 403 || status === 404) {
    return 'Если вы врач: открывайте упражнение из карточки пациента — /retell?patientId=UUID пациента.';
  }
  return null;
};

export default function RetellPage() {
  // phases: select | reading | running | saving | done | error
  const [phase, setPhase] = useState('select');
  const [stories, setStories] = useState(null);
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [doctorMode, setDoctorMode] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [listening, setListening] = useState(false);
  const [lastHint, setLastHint] = useState(null);   // {num, question} текущей подсказки
  const [hintPresses, setHintPresses] = useState(0);
  const [silenceAlert, setSilenceAlert] = useState(false); // попап «пациент молчит»
  const [speechNote, setSpeechNote] = useState('');  // «распознавание подтормаживает…»
  const [hintBusy, setHintBusy] = useState(false);   // запрос подсказки к серверу идёт
  const [hintError, setHintError] = useState('');    // причина, почему сервер не ответил
  const [hintDiag, setHintDiag] = useState(null);    // диагностика последней подсказки (для врача)

  // --- Web Speech API -------------------------------------------------------
  const recognitionRef = useRef(null);
  const spawnTimerRef = useRef(0);      // отложенное пересоздание сеанса (сериализация)
  const spawningRef = useRef(false);    // сеанс сейчас пересоздаётся — не дублировать
  const runningRef = useRef(false);
  const finalsRef = useRef([]);
  const interimRef = useRef('');
  const startedAtRef = useRef(0);
  const firstSpeechAtRef = useRef(0);
  const finishedRef = useRef(false);
  const lastSpeechAtRef = useRef(0);    // Date.now() последнего результата распознавания
  const silenceOpenRef = useRef(false); // попап тишины сейчас открыт
  const silenceTimerRef = useRef(0);    // ежесекундная проверка тишины
  const liveBoxRef = useRef(null);      // автопрокрутка окна стенограммы
  const watchdogRef = useRef(0);        // сторож: «микрофон слышит, распознавание молчит»
  const lastRestartAtRef = useRef(0);   // чтобы сторож не перезапускал чаще раза в 8 с
  const restartCountRef = useRef(0);    // сколько раз пересоздавали сеанс за пересказ
  const meterRef = useRef({ stream: null, ctx: null, timer: 0, loudAt: 0 });
  const hintBusyRef = useRef(false);    // не отправлять второй запрос подсказки, пока идёт первый
  const givenNumsRef = useRef([]);      // номера вех, по которым давали вопрос (по одному на нажатие)
  const hintEventsRef = useRef([]);     // протокол подсказок → уходит в отчёт

  const supported = useMemo(() => isRecognitionSupported(), []);

  const hintLimit = Number(session?.config?.hintLimit) > 0
    ? Number(session.config.hintLimit) : DEFAULT_HINT_LIMIT;
  const silenceMs = Number(session?.config?.silenceSec) > 0
    ? Number(session.config.silenceSec) * 1000 : DEFAULT_SILENCE_SEC * 1000;
  const milestones = useMemo(
    () => (Array.isArray(session?.milestones) ? session.milestones : []), [session]);

  const stopMeter = useCallback(() => {
    const m = meterRef.current;
    if (m.timer) window.clearInterval(m.timer);
    try { m.stream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { m.ctx?.close(); } catch { /* ignore */ }
    meterRef.current = { stream: null, ctx: null, timer: 0, loudAt: 0 };
  }, []);

  const stopRecognition = useCallback(() => {
    runningRef.current = false;
    window.clearInterval(watchdogRef.current);
    window.clearInterval(silenceTimerRef.current);
    window.clearTimeout(spawnTimerRef.current);
    spawningRef.current = false;
    stopMeter();
    setListening(false);
    try {
      recognitionRef.current?.stop();
    } catch { /* уже остановлен — не страшно */ }
    recognitionRef.current = null;
  }, [stopMeter]);

  const resetRunState = useCallback(() => {
    setResult(null); setFinalTranscript(''); setLiveText('');
    setLastHint(null); setHintPresses(0);
    silenceOpenRef.current = false;
    setSilenceAlert(false);
    setSpeechNote('');
    setHintError(''); setHintDiag(null);
    givenNumsRef.current = [];
    hintEventsRef.current = [];
  }, []);

  const openStory = useCallback(async (code, uid = patientId) => {
    setErrorMsg(''); setErrorHint('');
    resetRunState();
    try {
      const data = await getRetellStory(code);
      setSession(data);
      setPhase('reading');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось загрузить рассказ.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [patientId, resetRunState]);

  const loadStories = useCallback(async () => {
    setPhase('select');
    setErrorMsg(''); setErrorHint('');
    try {
      const params = new URLSearchParams(window.location.search);
      const pidOverride = params.get('patientId');
      setDoctorMode(Boolean(pidOverride));
      const uid = pidOverride || (await getAuthUserId());
      if (!uid) {
        setErrorMsg('Не удалось определить пользователя. Войдите в аккаунт и откройте упражнение заново.');
        setPhase('error');
        return;
      }
      setPatientId(uid);
      const res = await getRetellStories();
      const list = Array.isArray(res) ? res : res?.items || [];
      setStories(list);
      // ?story=t04 — сразу открыть рассказ (врач запускает конкретный).
      const want = String(params.get('story') || '').trim().toLowerCase();
      const found = list.find((s) => String(s.code || '').toLowerCase() === want);
      if (found) await openStory(found.code, uid);
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось загрузить список рассказов.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [openStory]);

  // Отправка transcript на сервер (счёт считает сервер).
  const finishRun = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    stopRecognition();
    const raw = [...finalsRef.current, interimRef.current].filter(Boolean).join(' ');
    const transcript = raw.replace(/\s+/g, ' ').trim().split(' ')
      .filter(Boolean).slice(0, MAX_WORDS).join(' ');
    setFinalTranscript(transcript);
    const elapsedMs = startedAtRef.current ? Date.now() - startedAtRef.current : null;
    // «На раздумье»: от старта пересказа до первого распознанного слова.
    const thinkingMs = startedAtRef.current
      ? (firstSpeechAtRef.current
        ? Math.max(0, firstSpeechAtRef.current - startedAtRef.current)
        : elapsedMs)
      : null;

    setPhase('saving');
    try {
      const res = await saveRetellReport({
        patientId,
        stepId: stepIdForStory(session?.num),
        transcript,
        elapsedMs,
        thinkingMs,
        hintsUsed: hintPresses,
        hintEvents: hintEventsRef.current,
        today: localToday(),
      });
      setResult(res);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось сохранить результат.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [patientId, session, hintPresses, stopRecognition]);

  const finishRef = useRef(finishRun);
  useEffect(() => { finishRef.current = finishRun; }, [finishRun]);

  useEffect(() => {
    console.info(`retell: сборка ${BUILD_ID}`);
    void loadStories();
  }, [loadStories]);

  // Тишина отсчитывается от последнего результата распознавания; попап
  // открывается раз на период молчания и закрывается речью или кнопкой.
  const armSilence = useCallback(() => {
    lastSpeechAtRef.current = Date.now();
    silenceOpenRef.current = false;
    setSilenceAlert(false);
  }, []);

  // Пересоздание сеанса распознавания. Chrome может молча терять поток
  // (речь идёт — событий нет): сторож и onend дёргают spawn, финальный
  // текст в finalsRef при этом не теряется.
  // СЕРИАЛИЗАЦИЯ: сначала глушим старый сеанс и даём Chrome ~300 мс его
  // закрыть, и только потом открываем новый. Если два сеанса живут
  // одновременно, Chrome отдаёт новому not-allowed (упражнение падало в
  // ошибку посреди пересказа) или оба молчат (текст переставал расти).
  const spawnRecognition = useCallback(() => {
    if (!runningRef.current || spawningRef.current) return;
    spawningRef.current = true;
    try { recognitionRef.current?.abort?.(); } catch { /* уже мёртв */ }
    recognitionRef.current = null;
    window.clearTimeout(spawnTimerRef.current);
    spawnTimerRef.current = window.setTimeout(() => {
      spawningRef.current = false;
      if (!runningRef.current) return;
      const rec = createRecognition({ lang: 'ru-RU', continuous: true, interimResults: true });
      if (!rec) return;
      rec.onresult = (event) => {
        lastSpeechAtRef.current = Date.now();   // речь есть — отсчёт тишины заново
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const res = event.results[i];
          const text = String(res?.[0]?.transcript || '').trim();
          if (!text) continue;
          if (!firstSpeechAtRef.current) firstSpeechAtRef.current = Date.now();
          if (res.isFinal) finalsRef.current.push(text);
          else interim += `${text} `;
        }
        interimRef.current = interim.trim();
        if (silenceOpenRef.current && (finalsRef.current.length || interimRef.current)) {
          silenceOpenRef.current = false;       // пациент снова говорит — попап не нужен
          setSilenceAlert(false);
        }
        setLiveText([...finalsRef.current, interimRef.current].filter(Boolean).join(' '));
      };
      rec.onerror = (event) => {
        const code = event?.error || '';
        if (code === 'not-allowed' || code === 'service-not-allowed') {
          if (!finalsRef.current.length && restartCountRef.current === 0) {
            // микрофон так и не разрешили — реальная ошибка доступа
            finishedRef.current = true;
            stopRecognition();
            setErrorMsg('Нет доступа к микрофону. Разрешите доступ (значок в адресной строке) и попробуйте ещё раз.');
            setPhase('error');
            return;
          }
          // посреди пересказа теряем только сеанс, а НЕ упражнение: текст
          // и кнопки на месте, сторож через пару секунд вернёт распознавание
          setSpeechNote('Связь с распознаванием оборвалась — восстанавливаем…');
          if (rec === recognitionRef.current) recognitionRef.current = null;
          return;
        }
        // no-speech / aborted / network — переживаем: onend/сторож перезапустят.
      };
      rec.onend = () => {
        // Chrome останавливается на паузах — открываем СВЕЖИЙ сеанс:
        // переиспользование старого экземпляра умеет молча умирать.
        if (runningRef.current && rec === recognitionRef.current) spawnRef.current();
      };
      recognitionRef.current = rec;
      try { rec.start(); } catch { window.setTimeout(() => spawnRef.current(), 500); }
    }, 300);
  }, [stopRecognition]);

  const spawnRef = useRef(spawnRecognition);
  useEffect(() => { spawnRef.current = spawnRecognition; }, [spawnRecognition]);

  // СТАРТ ПЕРЕСКАЗА: микрофон (по касанию — пользователь уже читал рассказ).
  const startRun = useCallback(async () => {
    // Микрофон нужен и распознаванию, и измерителю громкости (сторож).
    let stream = null;
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
    } catch {
      setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону в настройках браузера и попробуйте ещё раз.');
      setPhase('error');
      return;
    }

    finalsRef.current = [];
    interimRef.current = '';
    firstSpeechAtRef.current = 0;
    finishedRef.current = false;
    restartCountRef.current = 0;
    lastRestartAtRef.current = 0;
    setSpeechNote('');
    setLiveText('');
    startedAtRef.current = Date.now();
    runningRef.current = true;
    armSilence();
    setListening(true);
    setPhase('running');

    // Измеритель громкости: «в микрофоне есть речь». Если речь есть, а
    // результатов распознавания давно нет — сеанс умер, пересоздаём.
    if (stream) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = Ctx ? new Ctx() : null;
        const m = { stream, ctx, timer: 0, loudAt: Date.now() };
        if (ctx) {
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          ctx.createMediaStreamSource(stream).connect(analyser);
          const buf = new Float32Array(analyser.fftSize);
          m.timer = window.setInterval(() => {
            try {
              analyser.getFloatTimeDomainData(buf);
              let sum = 0;
              for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
              if (Math.sqrt(sum / buf.length) >= LOUD_RMS) m.loudAt = Date.now();
            } catch { /* контекст уже закрыт — не страшно */ }
          }, 300);
        }
        meterRef.current = m;
      } catch { /* нет метра — сторож работает только по событиям */ }
    }

    if (!supported) {
      runningRef.current = false;
      stopMeter();
      setErrorMsg('Браузер не поддерживает распознавание речи. Откройте упражнение в Google Chrome или Яндекс.Браузере.');
      setPhase('error');
      return;
    }
    spawnRef.current();  // сеанс откроется через ~300 мс (сериализация)

    // СТОРОЖ: микрофон слышит речь, а распознавание молчит → тихий перезапуск.
    window.clearInterval(watchdogRef.current);
    watchdogRef.current = window.setInterval(() => {
      if (!runningRef.current) return;
      const now = Date.now();
      // 1) сеанса нет вовсе (потеряли по ошибке) — вернуть при первом тике
      if (!recognitionRef.current && !spawningRef.current) {
        spawnRef.current();
        return;
      }
      // 2) речь в микрофоне есть, а текста нет — тихий перезапуск
      const micHasSpeech = now - (meterRef.current.loudAt || 0) < WATCHDOG_STALL_MS;
      const resultsFlow = now - lastSpeechAtRef.current < WATCHDOG_STALL_MS;
      if (micHasSpeech && !resultsFlow && now - lastRestartAtRef.current > WATCHDOG_STALL_MS) {
        lastRestartAtRef.current = now;
        restartCountRef.current += 1;
        setSpeechNote(restartCountRef.current >= 3
          ? 'Распознавание подтормаживает — текст может отставать. Проверьте интернет или микрофон.'
          : '');
        spawnRef.current();   // внутри: глушит старый сеанс, через 300 мс открывает новый
        return;
      }
      // 3) страховка на случай, если подвёл и измеритель громкости:
      //    результатов нет минуту — тихий перезапуск (на паузе он безвреден)
      if (now - lastSpeechAtRef.current >= 60000 && now - lastRestartAtRef.current > 30000) {
        lastRestartAtRef.current = now;
        spawnRef.current();
      }
    }, WATCHDOG_TICK_MS);

    // Тишина > silenceMs → попап «Нужна подсказка?» (раз на период молчания).
    window.clearInterval(silenceTimerRef.current);
    silenceTimerRef.current = window.setInterval(() => {
      if (!runningRef.current || silenceOpenRef.current) return;
      if (Date.now() - lastSpeechAtRef.current >= silenceMs) {
        silenceOpenRef.current = true;
        setSilenceAlert(true);
      }
    }, 1000);
  }, [silenceMs, supported, armSilence, stopMeter]);

  const startRunRef = useRef(startRun);
  useEffect(() => { startRunRef.current = startRun; }, [startRun]);

  // ПОДСКАЗКА: наводящий вопрос по МЕСТУ ОСТАНОВКИ. Веху выбирает СЕРВЕР
  // (POST /trainers/retell/hint): он считает закрытые вехи по текущей
  // стенограмме, находит границу рассказа и возвращает один вопрос. Клиент
  // ничего не угадывает: если сервер не ответил, показываем причину, а врач
  // выбирает вопрос вручную (см. «Другой вопрос» ниже). Нажатие считается и
  // пишется в протокол (hintEvents) только когда вопрос реально показан.
  const registerHint = useCallback((hint, event) => {
    givenNumsRef.current = [...givenNumsRef.current, Number(hint.num)];
    hintEventsRef.current = [...hintEventsRef.current, {
      num: Number(hint.num),
      atMs: startedAtRef.current ? Date.now() - startedAtRef.current : null,
      source: event.source,
      mode: event.mode || null,
      closedBefore: (event.closedBefore || []).map(Number),
    }];
    setHintPresses((n) => n + 1);
    setLastHint(hint);
  }, []);

  const giveHint = useCallback(async () => {
    if (!milestones.length || hintBusyRef.current || !session?.code) return;
    lastSpeechAtRef.current = Date.now();     // чтение вопроса вслух — не тишина
    if (hintPresses >= hintLimit) {
      setLastHint({ num: null, question: `Достигнут лимит подсказок (${hintLimit}).` });
      return;
    }
    hintBusyRef.current = true;
    setHintBusy(true);
    setHintError('');
    const t0 = Date.now();
    try {
      const raw = [...finalsRef.current, interimRef.current].filter(Boolean).join(' ');
      const res = await requestRetellHint({
        storyCode: session.code,
        transcript: raw.replace(/\s+/g, ' ').trim()
          .split(' ').filter(Boolean).slice(0, MAX_WORDS).join(' '),
        given: givenNumsRef.current,
        hintsUsed: hintPresses,
      });
      setHintDiag({
        api: res?.api || '?', mode: res?.mode, frontier: res?.frontier,
        closed: Array.isArray(res?.closed) ? res.closed : [],
        words: res?.wordCount, ms: Date.now() - t0,
      });
      if (res?.mode === 'live' && res?.target?.num != null) {
        const num = Number(res.target.num);
        registerHint(
          { num, question: res.target.question || `Веха №${num}`, again: !!res.again,
            skipped: !!res.skipped, partial: !!res.target.partial },
          { source: 'server', mode: res.mode, closedBefore: res.closed });
      } else if (res?.mode === 'limit') {
        setLastHint({ num: null, question: `Достигнут лимит подсказок (${hintLimit}).` });
      } else {
        setLastHint({
          num: null,
          question: 'Пересказ дошёл до конца рассказа — завершайте; что пропущено, врач увидит в отчёте.',
        });
      }
    } catch (err) {
      const status = err?.status ?? err?.response?.status;
      const why = status ? `HTTP ${status}` : 'нет связи с сервером';
      setHintError(why);
      setHintDiag({ api: 'нет ответа', mode: why, frontier: null, closed: [], words: null, ms: Date.now() - t0 });
      console.warn('retell: подсказка не получена —', why, err);
    } finally {
      hintBusyRef.current = false;
      setHintBusy(false);
    }
  }, [milestones, hintPresses, hintLimit, session, registerHint]);

  // Врач сам выбирает вопрос (сервер не ответил или выбрал не то).
  const giveManualHint = useCallback((numStr) => {
    const num = Number(numStr);
    const m = milestones.find((x) => Number(x.num) === num);
    if (!m || hintPresses >= hintLimit) return;
    lastSpeechAtRef.current = Date.now();
    setHintError('');
    registerHint(
      { num, question: m.hintQuestion || `Веха №${num}`, manual: true },
      { source: 'manual', mode: 'manual', closedBefore: hintDiag?.closed || [] });
  }, [milestones, hintPresses, hintLimit, hintDiag, registerHint]);

  // Кнопки попапа тишины: выдать подсказку или продолжить без неё.
  const dismissSilence = useCallback(() => {
    silenceOpenRef.current = false;
    setSilenceAlert(false);
    lastSpeechAtRef.current = Date.now();     // ждём тишину заново полные silenceMs
  }, []);

  const hintFromSilence = useCallback(() => {
    dismissSilence();
    void giveHint();
  }, [giveHint, dismissSilence]);

  // Предупреждение при случайном закрытии вкладки посреди пересказа.
  useEffect(() => {
    if (phase !== 'running') return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  // Стенограмма всегда прокручена к свежему тексту.
  useEffect(() => {
    const el = liveBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveText]);

  const backToSelect = useCallback(() => {
    stopRecognition();
    resetRunState();
    setSession(null);
    setPhase('select');
  }, [stopRecognition, resetRunState]);

  const retellAgain = useCallback(() => {
    stopRecognition();
    resetRunState();
    setPhase('reading');
  }, [stopRecognition, resetRunState]);

  // Размонтирование: остановить микрофон и все таймеры.
  useEffect(() => () => {
    runningRef.current = false;
    window.clearInterval(watchdogRef.current);
    window.clearInterval(silenceTimerRef.current);
    window.clearTimeout(spawnTimerRef.current);
    spawningRef.current = false;
    stopMeter();
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
  }, [stopMeter]);

  // ── рендер ────────────────────────────────────────────────────────────────
  const sr = result?.scoreResult || {};
  const blocks = Array.isArray(sr.blocks) ? sr.blocks : [];
  const flags = Array.isArray(sr.flags) ? sr.flags : [];
  const msResults = Array.isArray(sr.milestones) ? sr.milestones : [];
  const doctorInfo = session?.doctorInfo || null;
  const wordCount = liveText ? liveText.trim().split(/\s+/).length : 0;

  return (
    <div className="flex min-h-screen flex-col bg-cvx-background px-4 py-6">
      <main className={`mx-auto flex w-full flex-1 flex-col ${phase === 'running' || phase === 'saving' ? 'max-w-4xl' : 'max-w-xl'}`}>
        <header className="flex items-center justify-between gap-3">
          <a href="/" aria-label="Cortivox.ai на главную" className="flex items-center gap-2.5">
            <span className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white">
              <BrainCircuit className="size-4" />
            </span>
            <span className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">
              Cortivox<span className="text-cvx-logo-ai">.ai</span>
            </span>
          </a>
          <button type="button" onClick={() => window.history.back()} className="cvx-back">
            <ArrowLeft className="size-4" /> Назад
          </button>
        </header>

        {phase === 'select' && (
          <section className="mt-6 flex flex-1 flex-col">
            <h1 className="text-2xl font-bold tracking-tight text-cvx-foreground">Пересказ близко к тексту</h1>
            <p className="mt-1.5 text-sm leading-relaxed text-cvx-muted-foreground">
              Прочитайте рассказ вслух, затем попросите пациента пересказать его своими словами.
              Сейчас в банке {stories ? stories.length : '…'} из 20 рассказов.
            </p>
            <p className="mt-1 text-[10px] text-cvx-muted-foreground">сборка {BUILD_ID}</p>
            {stories == null && (
              <div className="mt-10 flex justify-center text-cvx-muted-foreground">
                <Loader2 className="size-6 animate-spin" />
              </div>
            )}
            {Array.isArray(stories) && stories.length === 0 && (
              <p className="mt-6 rounded-xl bg-cvx-muted p-4 text-sm text-cvx-muted-foreground">
                Рассказы ещё не добавлены в банк (retell_stories).
              </p>
            )}
            <div className="mt-5 flex flex-col gap-3">
              {(stories || []).map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => openStory(s.code)}
                  className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5 text-left transition hover:border-cvx-primary/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-cvx-muted-foreground">
                        {s.author}
                      </p>
                      <h3 className="mt-0.5 truncate text-lg font-bold text-cvx-foreground">«{s.title}»</h3>
                    </div>
                    <BookOpen className="size-5 shrink-0 text-cvx-primary" />
                  </div>
                  <p className="mt-2 text-xs text-cvx-muted-foreground">
                    вех: {Number(s.coreTotal) || 0} ядро
                    {Number(s.detailTotal) ? ` + ${s.detailTotal} деталь` : ''}
                    {' · '}блоков: {(Array.isArray(s.blocks) ? s.blocks : []).length || '—'}
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        {phase === 'reading' && session && (
          <section className="mt-6 flex flex-1 flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-cvx-muted-foreground">
              {session.author}
            </p>
            <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-cvx-foreground">«{session.title}»</h1>
            <p className="mt-1.5 text-sm leading-relaxed text-cvx-muted-foreground">
              Прочитайте рассказ вслух. Когда закончите — нажмите
              «Начать пересказ»: включится микрофон и пациент пересказывает своими словами.
            </p>
            <div className="cv-shadow mt-4 max-h-[52vh] overflow-y-auto rounded-2xl border border-cvx-border bg-cvx-card p-5">
              <p className="whitespace-pre-line text-[15px] leading-relaxed text-cvx-foreground">
                {session.storyText}
              </p>
            </div>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => startRunRef.current()}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cvx-primary px-5 py-3.5 font-semibold text-white transition hover:opacity-90"
              >
                <Mic className="size-5" /> Начать пересказ
              </button>
              <button
                type="button"
                onClick={backToSelect}
                className="rounded-xl border border-cvx-border px-5 py-3.5 font-semibold text-cvx-foreground transition hover:bg-cvx-muted"
              >
                Другой рассказ
              </button>
            </div>
          </section>
        )}

        {(phase === 'running' || phase === 'saving') && session && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {listening ? (
                  <span className="flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm font-semibold text-red-600">
                    <span className="relative flex size-2.5">
                      <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400" />
                      <span className="relative inline-flex size-2.5 rounded-full bg-red-500" />
                    </span>
                    Идёт запись
                  </span>
                ) : (
                  <span className="flex items-center gap-2 rounded-full bg-cvx-muted px-3 py-1 text-sm text-cvx-muted-foreground">
                    <MicOff className="size-3.5" /> микрофон выключен
                  </span>
                )}
              </div>
              <p className="text-sm tabular-nums text-cvx-muted-foreground">{wordCount} слов</p>
            </div>

            <h2 className="mt-3 text-lg font-bold text-cvx-foreground">«{session.title}»</h2>

            {speechNote && (
              <p className="mt-2 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
                <AlertTriangle className="size-3.5 shrink-0" />
                {speechNote}
              </p>
            )}

            <div
              ref={liveBoxRef}
              className="cv-shadow mt-3 min-h-[38vh] flex-1 overflow-y-auto rounded-2xl border border-cvx-border bg-cvx-card p-5"
            >
              <p className="min-h-12 whitespace-pre-wrap text-base leading-relaxed text-cvx-foreground">
                {liveText || <span className="text-cvx-muted-foreground">Пересказ появится здесь по мере речи…</span>}
              </p>
            </div>

            {lastHint && (
              <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
                  <Lightbulb className="size-4" />
                  Подсказка{lastHint.num ? ` · веха ${lastHint.num}` : ''}
                  {lastHint.manual ? ' · выбрано врачом' : ''}
                  {lastHint.partial ? ' · названа частично' : ''}
                  {lastHint.again ? ' · повтор вопроса' : ''}
                  {lastHint.skipped ? ' · следующая веха' : ''}
                </p>
                <p className="mt-1 text-[15px] font-medium leading-relaxed text-amber-900">
                  {lastHint.question}
                </p>
              </div>
            )}

            {hintError && (
              <p className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  Не удалось определить место в рассказе ({hintError}). Ничего не угадываем:
                  {doctorMode
                    ? ' выберите вопрос вручную в списке «Другой вопрос» ниже.'
                    : ' продолжайте рассказ, подсказка сейчас недоступна.'}
                </span>
              </p>
            )}

            {doctorMode && hintDiag && (
              <p className="mt-2 text-[11px] leading-snug text-cvx-muted-foreground">
                сборка {BUILD_ID} · сервер {hintDiag.api} · {hintDiag.mode}
                {hintDiag.frontier != null ? ` · граница ${hintDiag.frontier}` : ''}
                {hintDiag.closed?.length ? ` · закрыто [${hintDiag.closed.join(', ')}]` : ' · закрыто []'}
                {hintDiag.words != null ? ` · слов ${hintDiag.words}` : ''}
                {` · ${hintDiag.ms} мс`}
              </p>
            )}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={giveHint}
                disabled={phase !== 'running' || hintPresses >= hintLimit || hintBusy}
                className="flex items-center justify-center gap-2 rounded-xl border border-cvx-border bg-cvx-card px-5 py-3.5 font-semibold text-cvx-foreground transition hover:bg-cvx-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                {hintBusy ? <Loader2 className="size-5 animate-spin" /> : <Lightbulb className="size-5" />}
                Подсказка{hintPresses > 0 ? ` (${hintPresses}/${hintLimit})` : ''}
              </button>
              <button
                type="button"
                onClick={() => finishRef.current()}
                disabled={phase !== 'running'}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cvx-primary px-5 py-3.5 font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {phase === 'saving'
                  ? <Loader2 className="size-5 animate-spin" />
                  : <CheckCircle2 className="size-5" />}
                {phase === 'saving' ? 'Считаем…' : 'Готово'}
              </button>
            </div>
            {doctorMode && (
              <select
                aria-label="Другой вопрос (вручную)"
                value=""
                disabled={phase !== 'running' || hintPresses >= hintLimit}
                onChange={(e) => { if (e.target.value) giveManualHint(e.target.value); }}
                className="mt-2 w-full rounded-xl border border-cvx-border bg-cvx-card px-3 py-2.5 text-sm text-cvx-foreground disabled:opacity-50"
              >
                <option value="">Другой вопрос (выбрать вручную)…</option>
                {milestones.filter((m) => m.level === 'ядро').map((m) => (
                  <option key={m.num} value={m.num}>
                    №{m.num} · {String(m.hintQuestion || '').slice(0, 90)}
                  </option>
                ))}
              </select>
            )}
            {hintPresses >= hintLimit && (
              <p className="mt-2 text-xs text-amber-700">
                Достигнут лимит подсказок для этого пересказа ({hintLimit}).
              </p>
            )}
          </section>
        )}

        {phase === 'done' && result && (
          <section className="mt-6 flex flex-1 flex-col">
            <h1 className="text-2xl font-bold tracking-tight text-cvx-foreground">Пересказ сохранён</h1>
            <p className="mt-1 text-sm text-cvx-muted-foreground">
              «{result.title}» · {fmtElapsed(sr.elapsedMs)}
              {sr.thinkingMs != null ? ` · на раздумье ${fmtElapsed(sr.thinkingMs)}` : ''}
              {` · подсказок: ${Number(sr.hintsUsed) || 0}`}
            </p>

            <div className="cv-shadow mt-4 rounded-2xl border border-cvx-border bg-cvx-card p-5 text-center">
              <p className="text-5xl font-bold leading-none tabular-nums text-cvx-primary">
                {Number(sr.percent) || 0}%
              </p>
              <p className="mt-2 text-sm text-cvx-muted-foreground">
                ядро: {Number(sr.coreClosed) || 0} из {Number(sr.coreTotal) || 0} вех
                {Number(sr.detailTotal) > 0
                  ? ` · детали: ${Number(sr.detailClosed) || 0}/${sr.detailTotal}`
                  : ''}
              </p>
            </div>

            {blocks.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-cvx-foreground">По блокам сюжета</p>
                <div className="mt-2 flex flex-col gap-1.5">
                  {blocks.map((b) => (
                    <div key={b.name} className="flex items-center gap-3">
                      <span className="w-32 shrink-0 truncate text-xs text-cvx-muted-foreground">{b.name}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-cvx-muted">
                        <div
                          className="h-full rounded-full bg-cvx-primary"
                          style={{ width: `${b.coreTotal ? Math.round((b.coreClosed / b.coreTotal) * 100) : 0}%` }}
                        />
                      </div>
                      <span className="w-10 shrink-0 text-right text-xs tabular-nums text-cvx-muted-foreground">
                        {b.coreClosed}/{b.coreTotal}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {flags.length > 0 && (
              <div className="mt-4 flex flex-col gap-2">
                {flags.map((f) => (
                  <div key={f.type} className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3.5">
                    <Flag className="mt-0.5 size-4 shrink-0 text-amber-600" />
                    <p className="text-sm leading-relaxed text-amber-900">
                      <span className="font-semibold">{f.label || 'Флаг'}. </span>
                      {f.message}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {doctorMode && doctorInfo && (
              <div className="mt-5">
                <p className="text-sm font-semibold text-cvx-foreground">Вехи (сверка по стенограмме)</p>
                <div className="mt-2 flex max-h-80 flex-col gap-1.5 overflow-y-auto rounded-2xl border border-cvx-border bg-cvx-card p-3">
                  {msResults.map((m) => {
                    const st = STATUS[m.status] || STATUS.missed;
                    const info = doctorInfo[String(m.num)] || {};
                    const StIcon = st.icon;
                    return (
                      <div key={m.num} className="rounded-xl bg-cvx-muted/60 p-3">
                        <p className="flex items-start gap-2 text-sm">
                          <StIcon className={`mt-0.5 size-4 shrink-0 ${st.cls}`} />
                          <span className="text-cvx-foreground">
                            <span className="font-semibold">№{m.num}</span>{' '}
                            {info.formulation || ''}
                            {info.level === 'деталь' && (
                              <span className="ml-1 rounded bg-cvx-secondary px-1.5 py-0.5 text-[10px] font-semibold text-cvx-secondary-foreground">деталь</span>
                            )}
                          </span>
                        </p>
                        {m.matchedBy && (
                          <p className="ml-6 mt-1 text-xs text-cvx-muted-foreground">зачтено: {m.matchedBy}</p>
                        )}
                        {info.details && m.status !== 'closed' && (
                          <p className="ml-6 mt-1 text-xs leading-relaxed text-cvx-muted-foreground">
                            детали: {info.details}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {finalTranscript && (
              <details className="mt-4 rounded-2xl border border-cvx-border bg-cvx-card p-4">
                <summary className="cursor-pointer text-sm font-semibold text-cvx-foreground">
                  Стенограмма пересказа
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-cvx-muted-foreground">{finalTranscript}</p>
              </details>
            )}

            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={retellAgain}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cvx-primary px-5 py-3.5 font-semibold text-white transition hover:opacity-90"
              >
                <RotateCcw className="size-5" /> Пересказать ещё раз
              </button>
              <button
                type="button"
                onClick={backToSelect}
                className="rounded-xl border border-cvx-border px-5 py-3.5 font-semibold text-cvx-foreground transition hover:bg-cvx-muted"
              >
                Другой рассказ
              </button>
            </div>
          </section>
        )}

        {phase === 'error' && (
          <section className="mt-10 flex flex-1 flex-col items-center text-center">
            <AlertTriangle className="size-10 text-amber-500" />
            <h1 className="mt-3 text-xl font-bold text-cvx-foreground">Что-то пошло не так</h1>
            <p className="mt-2 text-sm leading-relaxed text-cvx-muted-foreground">{errorMsg}</p>
            {errorHint && <p className="mt-1 text-xs text-cvx-muted-foreground">{errorHint}</p>}
            <button
              type="button"
              onClick={() => { backToSelect(); void loadStories(); }}
              className="mt-6 rounded-xl bg-cvx-primary px-5 py-3 font-semibold text-white transition hover:opacity-90"
            >
              К списку рассказов
            </button>
          </section>
        )}

        {!supported && (phase === 'reading' || phase === 'running') && (
          <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            Браузер не поддерживает распознавание речи — пересказ можно ввести вручную на этапе
            результата (стенограмма) или открыть упражнение в Google Chrome / Яндекс.Браузере.
          </p>
        )}
      </main>

      {/* Попап тишины: пациент молчит дольше silenceSec — предложить подсказку. */}
      {silenceAlert && phase === 'running' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="alertdialog"
          aria-label="Пациент молчит — нужна подсказка?"
        >
          <div className="cv-shadow w-full max-w-md rounded-2xl border border-cvx-border bg-cvx-card p-6">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
              <Lightbulb className="size-4" /> Пациент молчит
            </p>
            <p className="mt-2 text-[15px] leading-relaxed text-cvx-foreground">
              Речи нет больше {Math.round(silenceMs / 1000)} секунд.
              Нужна подсказка — наводящий вопрос по следующей вехе рассказа?
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={hintFromSilence}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cvx-primary px-5 py-3 font-semibold text-white transition hover:opacity-90"
              >
                <Lightbulb className="size-4" /> Показать подсказку
              </button>
              <button
                type="button"
                onClick={dismissSilence}
                className="rounded-xl border border-cvx-border px-5 py-3 font-semibold text-cvx-foreground transition hover:bg-cvx-muted"
              >
                Продолжить без подсказки
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
